import React, { useId } from "react";
import PropTypes from "prop-types";
import { TbLock } from "react-icons/tb";
import { formatPhoneBR } from "../../../utils/phone";
import { getSafePhone, getSafeUsername } from "../../../utils/identity";
import styles from "./IdentityBadge.module.css";

/**
 * IdentityBadge — indicador do estado de identidade de um contato WhatsApp
 * (ADR-0016 / Fase 5). Nunca renderiza BSUID cru.
 *
 *  - state="phone"    → exibe o telefone formatado (ou nada, se ausente).
 *  - state="username" → exibe `@handle`.
 *  - state="hidden"   → chip "Via username" com ícone de cadeado + tooltip
 *                       acessível (por mouse e por teclado) explicando o recurso.
 *
 * A11y: o chip "hidden" é focável, tem `aria-label` descritivo e um tooltip
 * associado por `aria-describedby`, visível em `:hover` e `:focus-visible`.
 */

const HIDDEN_TOOLTIP =
  "Telefone não compartilhado — este contato usa o novo recurso de nome de usuário do WhatsApp.";

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

export default function IdentityBadge({
  state,
  username,
  phone,
  size = "md",
  className,
}) {
  const tooltipId = useId();
  const sizeClass = size === "sm" ? styles.sm : styles.md;
  const safeUsername = getSafeUsername(username);
  const safePhone = getSafePhone(phone);

  // Estado "username" só se realmente houver handle; senão degrada para "hidden".
  if (state === "username" && safeUsername) {
    return (
      <span
        className={cx(styles.username, sizeClass, className)}
        aria-label={`Nome de usuário do WhatsApp: @${safeUsername}`}
      >
        @{safeUsername}
      </span>
    );
  }

  if (state === "phone") {
    const formatted = formatPhoneBR(safePhone);
    if (!formatted) return null;
    return (
      <span
        className={cx(styles.phone, sizeClass, className)}
        aria-label={`Telefone do WhatsApp: ${formatted}`}
      >
        {formatted}
      </span>
    );
  }

  // "hidden" (e "username" sem handle) — chip explicativo com tooltip.
  return (
    <span
      className={cx(styles.hidden, sizeClass, className)}
      tabIndex={0}
      role="note"
      aria-label={`Via username. ${HIDDEN_TOOLTIP}`}
      aria-describedby={tooltipId}
    >
      <TbLock className={styles.icon} aria-hidden="true" focusable="false" />
      <span className={styles.hiddenLabel}>Via username</span>
      <span role="tooltip" id={tooltipId} className={styles.tooltip}>
        {HIDDEN_TOOLTIP}
      </span>
    </span>
  );
}

IdentityBadge.propTypes = {
  state: PropTypes.oneOf(["phone", "hidden", "username"]).isRequired,
  username: PropTypes.string,
  phone: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  size: PropTypes.oneOf(["sm", "md"]),
  className: PropTypes.string,
};
